import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, fmtNum, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Printer, ArrowLeft, CheckCheck, Send, X as XIcon, Trash2, FileText } from "lucide-react";
import RackDiagram from "@/components/RackDiagram";
import ChargesEditor from "@/components/ChargesEditor";
import PalletBomModal from "@/components/PalletBomModal";

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";
  const canUseBom = user?.role === "admin" || user?.role === "employee";
  const canEditCharges = user?.role === "admin" || user?.role === "employee";
  const [q, setQ] = useState(null);

  // BOM state
  const [bomOpen, setBomOpen] = useState(false);
  const [bomRows, setBomRows] = useState([]);
  const [bomBusy, setBomBusy] = useState(false);

  useEffect(() => {
    api.get(`/quotations/${id}`).then((r) => setQ(r.data));
  }, [id]);

  const changeStatus = async (status) => {
    const { data } = await api.patch(`/quotations/${id}/status`, { status });
    setQ(data);
    toast.success(`Marked ${status}`);
  };

  const del = async () => {
    if (!window.confirm("Delete this quotation?")) return;
    await api.delete(`/quotations/${id}`);
    toast.success("Deleted");
    navigate("/quotations");
  };

  const openBom = async () => {
    setBomOpen(true);
    setBomBusy(true);
    setBomRows([]);
    try {
      const { data } = await api.get(`/quotations/${id}/bom`);
      setBomRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBomOpen(false);
    } finally {
      setBomBusy(false);
    }
  };

  if (!q) {
    return (
      <Layout>
        <div className="p-12 label-tech">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="no-print">
        <PageHeader
          eyebrow={`Quotation · ${q.status}`}
          title={q.quote_number}
          subtitle={`Created ${new Date(q.created_at).toLocaleString("en-IN")} by ${q.created_by_name}`}
          actions={
            <div className="flex items-center gap-2">
              <button onClick={() => navigate(-1)} className="btn-ghost" data-testid="back-btn">
                <ArrowLeft size={16} /> Back
              </button>
              {canUseBom && (
                <button onClick={openBom} className="btn-secondary" data-testid="pr-detail-bom-btn">
                  <FileText size={16} /> BOM
                </button>
              )}
              <button onClick={() => window.print()} className="btn-primary" data-testid="print-btn">
                <Printer size={16} /> Print / PDF
              </button>
            </div>
          }
        />

        <div className="px-8 pt-6 flex flex-wrap items-center gap-2">
          <span className="label-tech mr-2">Actions</span>
          <button onClick={() => changeStatus("sent")} className="btn-secondary" data-testid="status-sent">
            <Send size={14} /> Mark Sent
          </button>
          <button onClick={() => changeStatus("approved")} className="btn-secondary" data-testid="status-approved">
            <CheckCheck size={14} /> Approved
          </button>
          <button onClick={() => changeStatus("rejected")} className="btn-secondary" data-testid="status-rejected">
            <XIcon size={14} /> Rejected
          </button>
          {user.role === "admin" && (
            <button onClick={del} className="btn-danger ml-auto" data-testid="delete-btn">
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="p-8">
        <div className="panel-heavy max-w-5xl mx-auto" data-testid="pdf-view">
          {/* Header */}
          <div className="p-8 border-b-2 border-ink flex items-start justify-between">
            <div className="flex items-start gap-5">
              <img
                src="/brand/adarsh-logo.png"
                alt="Adarsh Udyog"
                className="h-20 w-auto object-contain"
              />
              <div>
                <div className="label-tech mt-1">A Complete Storage Solution</div>
                <div className="text-xs text-ink-muted mt-3 mono">
                  Plot No. 2, Nangli Sakrawati Industrial Area <br />
                  adarshudyog@gmail.com · +91 98100 98860
                  {q.company_gstin ? (
                    <>
                      <br />GSTIN: {q.company_gstin}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="label-tech">Quotation No.</div>
              <div className="mono font-bold text-2xl mt-1">{q.quote_number}</div>
              <div className="label-tech mt-4">Date</div>
              <div className="mono text-sm mt-1">{new Date(q.created_at).toLocaleDateString("en-IN")}</div>
              <div className="label-tech mt-4">Status</div>
              <div className="mono font-bold uppercase">{q.status}</div>
            </div>
          </div>

          {/* Client + Summary */}
          <div className="p-8 grid grid-cols-2 gap-8 border-b border-line">
            <div>
              <div className="label-tech mb-3">Billed to</div>
              <div className="font-bold text-ink">{q.client?.company || "—"}</div>
              <div className="text-sm text-ink-muted">{q.client?.name}</div>
              <div className="text-sm text-ink-muted">{q.client?.contact}</div>
              <div className="text-sm text-ink-muted">{q.client?.address}</div>
              {q.client?.gstin && <div className="mono text-xs mt-2">GSTIN: {q.client.gstin}</div>}
            </div>
            <div>
              <div className="label-tech mb-3">Summary</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <span className="text-ink-muted">Line items</span>
                <span className="mono font-bold text-right">{q.items.length}</span>
                <span className="text-ink-muted">Total racks</span>
                <span className="mono font-bold text-right">
                  {q.items.reduce((a, i) => a + i.quantity, 0)}
                </span>
                <span className="text-ink-muted">Total weight</span>
                <span className="mono font-bold text-right">{fmtNum(q.total_weight_kg, 2)} kg</span>
                <span className="text-ink-muted">Subtotal</span>
                <span className="mono font-bold text-right">{fmtINR(q.total_price)}</span>
                <span className="text-ink-muted">GST @ {fmtNum(q.gst_percent || 0, 0)}%</span>
                <span className="mono font-bold text-right">{fmtINR(q.gst_amount || 0)}</span>
                {(q.freight_amount || 0) > 0 && (
                  <>
                    <span className="text-ink-muted">Freight</span>
                    <span className="mono font-bold text-right">{fmtINR(q.freight_amount)}</span>
                  </>
                )}
                {(q.installation_amount || 0) > 0 && (
                  <>
                    <span className="text-ink-muted">Installation</span>
                    <span className="mono font-bold text-right">{fmtINR(q.installation_amount)}</span>
                  </>
                )}
                <span className="text-ink-muted">Grand total</span>
                <span className="mono font-bold text-right text-brand">{fmtINR(q.grand_total_price || q.total_price)}</span>
              </div>
            </div>
          </div>

          {/* Line items summary */}
          <div className="p-8 pb-0">
            <div className="label-tech mb-3">Line items</div>
            <table className="w-full grid-lines">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Label</th>
                  <th>Spec</th>
                  <th className="num">Approx unit wt</th>
                  <th className="num">Unit price</th>
                  <th className="num">Qty</th>
                  <th className="num">Line total</th>
                </tr>
              </thead>
              <tbody>
                {q.items.map((it, idx) => (
                  <tr key={idx}>
                    <td className="mono">{idx + 1}</td>
                    <td className="label-tech">{it.config.unit_type === "addon" ? "Add-on" : "Main"}</td>
                    <td>{it.label || "—"}</td>
                    <td className="mono text-xs">
                      {it.config.height}×{it.config.length}×{it.config.depth} · {it.config.levels}L · {it.config.weight_capacity_per_level}kg
                    </td>
                    <td className="num">{fmtNum(it.result.total_weight_kg, 2)} kg</td>
                    <td className="num">{fmtINR(it.result.total_price)}</td>
                    <td className="num">{it.quantity}</td>
                    <td className="num font-bold">{fmtINR(it.result.total_price * it.quantity)}</td>
                  </tr>
                ))}
                <tr className="bg-surfaceAlt">
                  <td colSpan={6}></td>
                  <td className="num label-tech">Subtotal</td>
                  <td className="num font-bold mono">{fmtINR(q.total_price)}</td>
                </tr>
                <tr className="bg-surfaceAlt">
                  <td colSpan={6}></td>
                  <td className="num label-tech">GST @ {fmtNum(q.gst_percent || 0, 0)}%</td>
                  <td className="num font-bold mono">{fmtINR(q.gst_amount || 0)}</td>
                </tr>
                {(q.freight_amount || 0) > 0 && (
                  <tr className="bg-surfaceAlt">
                    <td colSpan={6}></td>
                    <td className="num label-tech">Freight</td>
                    <td className="num font-bold mono">{fmtINR(q.freight_amount)}</td>
                  </tr>
                )}
                {(q.installation_amount || 0) > 0 && (
                  <tr className="bg-surfaceAlt">
                    <td colSpan={6}></td>
                    <td className="num label-tech">Installation</td>
                    <td className="num font-bold mono">{fmtINR(q.installation_amount)}</td>
                  </tr>
                )}
                <tr className="bg-surfaceAlt">
                  <td colSpan={6}></td>
                  <td className="num label-tech">Grand Total</td>
                  <td className="num font-black text-brand mono">{fmtINR(q.grand_total_price || q.total_price)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Per-item details */}
          {q.items.map((it, idx) => {
            const specRows = ["Vertical", "Beam", "Panel"].map((name) => {
              const c = it.result.components.find((x) => x.component === name);
              return { name, section: c?.section_type || "—", thickness: c?.thickness ?? null };
            });
            return (
              <div key={idx} className="p-8 border-t border-line" data-testid={`qitem-${idx}`}>
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                  <div>
                    <div className="label-tech">
                      Item {idx + 1} · {it.config.unit_type === "addon" ? "Add-on" : "Main"}
                      {it.label && ` · ${it.label}`}
                    </div>
                    <div className="display font-black text-xl tracking-tighter uppercase">
                      {it.quantity} × {it.config.height}×{it.config.length}×{it.config.depth} mm ·{" "}
                      {it.config.levels}L · {it.config.weight_capacity_per_level} kg/lv
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="label-tech">Line total</div>
                    <div className="display font-black text-2xl text-brand">
                      {fmtINR(it.result.total_price * it.quantity)}
                    </div>
                  </div>
                </div>

                {/* Compact 3-row spec table (Vertical / Beam / Panel) */}
                <table className="w-full grid-lines" data-testid={`qitem-${idx}-spec`}>
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Section</th>
                      <th className="num">Thickness</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specRows.map((r) => (
                      <tr key={r.name}>
                        <td className="font-bold uppercase tracking-wider text-xs">{r.name}</td>
                        <td>{r.section}</td>
                        <td className="num">{r.thickness ? `${r.thickness} mm` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-5">
                  <RackDiagram config={it.config} components={it.result.components} sideBySide />
                </div>
              </div>
            );
          })}

          {/* Extra charges — editable by admin/employee, prints when non-zero */}
          {canEditCharges && (
            <ChargesEditor
              endpoint={`/quotations/${id}/charges`}
              freight={q.freight_amount}
              installation={q.installation_amount}
              onUpdated={setQ}
            />
          )}

          {/* Grand Totals */}
          <div className="p-8 border-t-2 border-ink">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <div className="label-tech">Total Weight</div>
                <div className="mono text-2xl font-bold mt-1">{fmtNum(q.total_weight_kg, 2)} kg</div>
              </div>
              <div>
                <div className="label-tech">Line items</div>
                <div className="mono text-2xl font-bold mt-1">{q.items.length}</div>
              </div>
              <div className="sm:text-right space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="label-tech">Subtotal</span>
                  <span className="mono font-bold">{fmtINR(q.total_price)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="label-tech">GST @ {fmtNum(q.gst_percent || 0, 0)}%</span>
                  <span className="mono font-bold">{fmtINR(q.gst_amount || 0)}</span>
                </div>
                {(q.freight_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="label-tech">Freight</span>
                    <span className="mono font-bold">{fmtINR(q.freight_amount)}</span>
                  </div>
                )}
                {(q.installation_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="label-tech">Installation</span>
                    <span className="mono font-bold">{fmtINR(q.installation_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t-2 border-ink">
                  <span className="label-tech">Grand Total</span>
                  <span className="display font-black text-3xl text-brand">{fmtINR(q.grand_total_price || q.total_price)}</span>
                </div>
              </div>
            </div>
          </div>

          {q.notes && (
            <div className="p-8 pt-0 border-t border-line">
              <div className="label-tech mb-2">Notes</div>
              <div className="text-sm text-ink whitespace-pre-wrap">{q.notes}</div>
            </div>
          )}

          <div className="p-8 pt-4 border-t border-line text-xs text-ink-muted">
            This quotation is valid for 7 days from the date of issue and is subject to final approval. Taxes as applicable.
          </div>
        </div>
      </div>

      {bomOpen && (
        <PalletBomModal
          rows={bomRows}
          busy={bomBusy}
          client={q.client || {}}
          quoteNumber={q.quote_number}
          items={q.items || []}
          onClose={() => setBomOpen(false)}
        />
      )}
    </Layout>
  );
}
